import React from "react";

/**
 * Long-scroll editorial body blocks for the Hybrid and AI-Led case studies.
 * Rendered under the case-study video on /capabilities/:slug detail pages.
 * Styling stays inside the .dark-block cinematic scope of CapabilityDetail.
 */

const geistFont =
  "'Geist', 'Inter', ui-sans-serif, system-ui, sans-serif";

const Kicker = ({ children }) => (
  <p className="text-[10px] md:text-[11px] tracking-[0.4em] uppercase text-ink/55">
    {children}
  </p>
);

const Section = ({ children, className = "" }) => (
  <section className={`py-12 md:py-16 ${className}`}>{children}</section>
);

const Prose = ({ children }) => (
  <p
    className="text-ink/80 text-[15px] md:text-base leading-[1.7] font-light tracking-[-0.005em]"
    style={{ fontFamily: geistFont }}
  >
    {children}
  </p>
);

const NumberedBlock = ({ number, title, children }) => (
  <div className="grid grid-cols-1 md:grid-cols-[80px_1fr] gap-6 md:gap-10 py-10 border-t border-ink/12">
    <span className="text-[11px] tracking-[0.4em] uppercase text-ink/55">
      {number}
    </span>
    <div>
      <h4 className="font-heading uppercase leading-[1.15] tracking-[-0.01em] text-[18px] md:text-[22px] mb-4">
        {title}
      </h4>
      <Prose>{children}</Prose>
    </div>
  </div>
);

const PullQuote = ({ children }) => (
  <blockquote className="my-14 md:my-20 font-serif italic text-2xl md:text-3xl leading-[1.35] text-ink/90 max-w-3xl">
    &ldquo;{children}&rdquo;
  </blockquote>
);

const BuysCard = ({ eyebrow, title, items }) => (
  <div className="rounded-2xl border border-ink/12 p-7 md:p-8 hover:border-ink/30 transition-colors">
    <Kicker>{eyebrow}</Kicker>
    <h4 className="mt-3 font-heading uppercase leading-[1.15] tracking-[-0.01em] text-[18px] md:text-[20px] mb-5">
      {title}
    </h4>
    <ul className="space-y-3">
      {items.map((it, i) => (
        <li
          key={i}
          className="flex gap-3 text-[15px] leading-[1.55] text-ink/80 font-light"
          style={{ fontFamily: geistFont }}
        >
          <span className="text-ink/40 mt-[7px] w-1.5 h-1.5 rounded-full bg-ink/40 shrink-0" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  </div>
);

const ClosingCase = ({ label, children }) => (
  <div className="mt-16 md:mt-24 rounded-2xl bg-ink/[0.04] border border-ink/12 px-7 md:px-10 py-10 md:py-12">
    <Kicker>— {label}</Kicker>
    <div className="mt-5 max-w-3xl">
      <Prose>{children}</Prose>
    </div>
  </div>
);

/**
 * Universal case-study footer — a compact editorial band. Two columns split
 * by a hairline divider. No card borders, no full-bleed background band.
 * Accepts 1–3 columns; reusable across every capability case study.
 */
export const CaseStudyFooter = ({ label, columns }) => {
  const count = columns.length;
  return (
    <section
      className="mt-24 md:mt-32 pt-16 md:pt-20 border-t border-ink/12"
      data-testid="case-study-footer"
    >
      <p className="text-center text-[10px] md:text-[11px] tracking-[0.4em] uppercase text-ink/55">
        {label}
      </p>

      <div
        className={[
          "mt-14 md:mt-16 grid grid-cols-1 gap-y-14",
          count === 1 && "md:grid-cols-1",
          count === 2 && "md:grid-cols-2",
          count === 3 && "md:grid-cols-3",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {columns.map((c, i) => {
          const isFirst = i === 0;
          const isLast = i === count - 1;
          return (
            <div
              key={i}
              className={[
                "flex flex-col px-2 md:px-10",
                !isLast ? "md:border-r border-ink/12" : "",
                !isFirst ? "border-t md:border-t-0 border-ink/12 pt-14 md:pt-0" : "",
              ].join(" ")}
              data-testid={`case-study-footer-col-${i}`}
            >
              <span className="text-[11px] tracking-[0.4em] uppercase text-ink/55">
                {c.eyebrow}
              </span>
              <h4 className="mt-4 font-heading font-black uppercase leading-[1.05] tracking-[-0.02em] text-[22px] md:text-[28px]">
                {c.title}
              </h4>
              {c.subtitle && (
                <p className="mt-3 font-serif italic text-[15px] md:text-[17px] text-ink/70 leading-[1.4]">
                  {c.subtitle}
                </p>
              )}
              <ul className="mt-7 space-y-3.5">
                {c.items.map((it, j) => (
                  <li
                    key={j}
                    className="flex gap-4 items-baseline text-[14px] md:text-[15px] leading-[1.55] text-ink/85 font-light"
                    style={{ fontFamily: geistFont }}
                  >
                    <span className="text-ink/40 text-[10px] tracking-[0.28em] uppercase font-normal shrink-0 pt-[3px] w-7">
                      {String(j + 1).padStart(2, "0")}
                    </span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
};

/* ============================================================
 * HYBRID PRODUCTION
 * ==========================================================*/
export const HybridBody = () => (
  <div
    className="mx-auto w-full max-w-6xl px-6 md:px-0 mt-10"
    data-testid="hybrid-body"
  >
    {/* Three pillars — The world / The people / The film — static cards */}
    <Section className="!pt-8 md:!pt-12">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10" data-testid="hybrid-pillars">
        {[
          {
            title: "The world",
            body:
              "Before anyone stands in frame, we explore the film world wide with multiple full looks of the location, reviewed together until one image is locked by everyone. From that moment the world can\u2019t drift: every frame of every film answers to the look you signed off.",
          },
          {
            title: "The people",
            body:
              "The cast and product are filmed once, clean. Then each frame of the edit is composed with diffusion models. The real person placed into the locked world, with framing, light and shadow worked until nothing looks cut out or pasted. Pose, face, product: exactly as shot, in every frame. The person is never left to interpret. Only the world is.",
          },
          {
            title: "The film",
            body:
              "With every keyframe locked and approved, the film is brought to motion. Real performances animated inside their generated worlds, then cut to match the approved edit. What you signed off is what moves.",
          },
        ].map((p) => (
          <article
            key={p.title}
            className="group relative flex flex-col justify-between h-full rounded-2xl border border-ink/12 p-7 md:p-8 hover:border-ink/40 hover:-translate-y-1 transition-all duration-500"
            data-testid={`hybrid-pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div>
              <h4 className="font-heading font-medium uppercase leading-[1.05] tracking-[-0.01em] text-[22px] md:text-[26px]">
                {p.title}
              </h4>
              <p
                className="mt-5 text-sm md:text-[15px] text-ink/70 leading-[1.6] font-light"
                style={{ fontFamily: geistFont }}
              >
                {p.body}
              </p>
            </div>
          </article>
        ))}
      </div>
    </Section>

    <CaseStudyFooter
      label="What Hybrid buys a brand"
      columns={[
        {
          eyebrow: "Control & Trust",
          title: "Everything on-brand stays real",
          subtitle: "Nothing you approve ever drifts.",
          items: [
            "Real cast, real product, real performance \u2014 captured, not imagined",
            "One locked world, consistent across every film and cut-down",
            "Approval gates at every stage \u2014 you sign off before anything moves",
          ],
        },
        {
          eyebrow: "Freedom & Speed",
          title: "The world becomes a variable",
          subtitle: "Locations turn from logistics into choices.",
          items: [
            "Locations explored in hours.",
            "Worlds styled exactly to the brand",
            "One shoot, many films!",
          ],
        },
      ]}
    />
  </div>
);

/**
 * Inline before/after image pair for use inside a case-study body.
 * Mirrors the ImagePair renderer in CapabilityDetail but scoped for editorial
 * flow inside the max-w-4xl body column.
 */
const InlineImagePair = ({ before, after, index }) => (
  <div
    className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6"
    data-testid={`ai-body-image-pair-${index}`}
  >
    {[before, after].map((side, i) => (
      <div key={i} className="relative">
        <span className="absolute top-3 left-3 z-10 text-[10px] tracking-[0.32em] uppercase text-ink/85 bg-canvas/60 backdrop-blur-sm border border-ink/20 rounded-full px-3 py-1">
          {side.label}
        </span>
        <img
          src={side.src}
          alt={`AI-Led case study — ${side.label}`}
          className="w-full aspect-video object-cover rounded-2xl bg-black shadow-2xl shadow-black/40"
        />
      </div>
    ))}
  </div>
);

/* ============================================================
 * AI-LED PRODUCTION
 * ==========================================================*/
const AiLedTitleCards = () => {
  const cards = [
    {
      title: "The Seed",
      body:
        "We start from the smallest real seed \u2014 a product photograph, a reference still, a pack shot \u2014 and generate outward from that single anchor.",
    },
    {
      title: "The Pipeline",
      body:
        "Assets, characters, environments and motion are built and directed inside AI, held to the same review gates and craft discipline as a full shoot.",
    },
    {
      title: "The Output",
      body:
        "Every asset was generated in AI. A short studio session for framing stills; motion directed through prompts. No location, no crew \u2014 full cinematic control.",
    },
  ];
  return (
    <Section className="!pt-4 md:!pt-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10" data-testid="ai-cards">
        {cards.map((p) => (
          <article
            key={p.title}
            className="group relative flex flex-col justify-between h-full rounded-2xl border border-ink/12 p-7 md:p-8 hover:border-ink/40 hover:-translate-y-1 transition-all duration-500"
            data-testid={`ai-card-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div>
              <h4 className="font-heading font-medium uppercase leading-[1.05] tracking-[-0.01em] text-[22px] md:text-[26px]">
                {p.title}
              </h4>
              <p
                className="mt-5 text-sm md:text-[15px] text-ink/70 leading-[1.6] font-light"
                style={{ fontFamily: geistFont }}
              >
                {p.body}
              </p>
            </div>
          </article>
        ))}
      </div>
    </Section>
  );
};

const AiLedFooter = () => (
  <CaseStudyFooter
    label="What AI-Led buys a brand"
    columns={[
      {
        eyebrow: "Speed & Scale",
        title: "Endless formats from one look",
        subtitle: "Ideas hit air in days, not months.",
        items: [
          "Concept to finished asset in days, not weeks",
          "One look, endless formats and variations",
        ],
      },
      {
        eyebrow: "Cost & Control",
        title: "Budget freed for reach",
        subtitle: "No crew, no travel \u2014 only intent.",
        items: [
          "No location, no crew, no travel \u2014 budget freed for reach",
          "Every asset reviewed and locked, never left to chance",
        ],
      },
    ]}
  />
);

export const AiLedBody = () => {
  // Temporary preview switch for evaluating layouts. Reads ?opt=a|b|c.
  const opt =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("opt")
      : null;

  const Pair1 = (
    <InlineImagePair
      index={0}
      before={{ src: "/capabilities/ai-before-1.png", label: "Before 1" }}
      after={{ src: "/capabilities/ai-after-1.png", label: "After 1" }}
    />
  );
  const Pair2 = (
    <InlineImagePair
      index={1}
      before={{ src: "/capabilities/ai-before-2.png", label: "Before 2" }}
      after={{ src: "/capabilities/ai-after-2.png", label: "After 2" }}
    />
  );
  const HeroCaption = (
    <p
      className="mx-auto max-w-3xl text-center text-sm md:text-base text-ink/85 leading-[1.6] font-light tracking-[-0.005em] mt-8"
      style={{ fontFamily: geistFont }}
    >
      Every asset was generated in AI. A short studio session for framing
      stills; motion directed through prompts. No location, no crew &mdash;
      full cinematic control.
    </p>
  );

  // ---- Option A: two hero image-pair blocks stacked + 3 cards + footer
  if (opt === "a") {
    return (
      <div className="mx-auto w-full max-w-6xl px-6 md:px-0 mt-4" data-testid="ai-led-body">
        {Pair1}
        <div className="mt-8 md:mt-10">{Pair2}</div>
        {HeroCaption}
        <AiLedTitleCards />
        <AiLedFooter />
      </div>
    );
  }

  // ---- Option B: single 2x2 grid as one hero block + 3 cards + footer
  if (opt === "b") {
    return (
      <div className="mx-auto w-full max-w-6xl px-6 md:px-0 mt-4" data-testid="ai-led-body">
        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6"
          data-testid="ai-hero-grid"
        >
          {[
            { src: "/capabilities/ai-before-1.png", label: "Before 1" },
            { src: "/capabilities/ai-after-1.png", label: "After 1" },
            { src: "/capabilities/ai-before-2.png", label: "Before 2" },
            { src: "/capabilities/ai-after-2.png", label: "After 2" },
          ].map((s, i) => (
            <div key={i} className="relative">
              <span className="absolute top-3 left-3 z-10 text-[10px] tracking-[0.32em] uppercase text-ink/85 bg-canvas/60 backdrop-blur-sm border border-ink/20 rounded-full px-3 py-1">
                {s.label}
              </span>
              <img
                src={s.src}
                alt={`AI-Led case study — ${s.label}`}
                className="w-full aspect-video object-cover rounded-2xl bg-black shadow-2xl shadow-black/40"
              />
            </div>
          ))}
        </div>
        {HeroCaption}
        <AiLedTitleCards />
        <AiLedFooter />
      </div>
    );
  }

  // ---- Option C: both pairs stacked with no text between + 3 cards + footer
  if (opt === "c") {
    return (
      <div className="mx-auto w-full max-w-6xl px-6 md:px-0 mt-4" data-testid="ai-led-body">
        {Pair1}
        <div className="mt-6 md:mt-8">{Pair2}</div>
        {HeroCaption}
        <AiLedTitleCards />
        <AiLedFooter />
      </div>
    );
  }

  // ---- Default: matches Hybrid + Content Engine uniformity
  //      Hero slideshow renders above via CASE_STUDIES.ai media loop;
  //      body just carries the cards + universal footer.
  return (
    <div
      className="mx-auto w-full max-w-6xl px-6 md:px-0 mt-10"
      data-testid="ai-led-body"
    >
      <AiLedTitleCards />
      <AiLedFooter />
    </div>
  );
};

/* ============================================================
 * CONTENT ENGINE
 * ==========================================================*/
export const EnginesBody = () => {
  const cards = [
    {
      title: "Lock the world",
      body:
        "Style, characters, brand system and rules, signed off once.",
    },
    {
      title: "Wire the workflow",
      body:
        "Model selection, prompt architecture and review gates \u2014 so quality is repeatable, not lucky.",
    },
    {
      title: "Produce at scale",
      body:
        "Feed it the next brief; out comes on-brand work in every format, on demand.",
    },
  ];
  return (
    <div
      className="mx-auto w-full max-w-6xl px-6 md:px-0 mt-10"
      data-testid="engines-body"
    >
      {/* Three pillars — same static card pattern as Hybrid */}
      <Section className="!pt-8 md:!pt-12">
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10"
          data-testid="engines-cards"
        >
          {cards.map((p) => (
            <article
              key={p.title}
              className="group relative flex flex-col justify-between h-full rounded-2xl border border-ink/12 p-7 md:p-8 hover:border-ink/40 hover:-translate-y-1 transition-all duration-500"
              data-testid={`engines-card-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div>
                <h4 className="font-heading font-medium uppercase leading-[1.05] tracking-[-0.01em] text-[22px] md:text-[26px]">
                  {p.title}
                </h4>
                <p
                  className="mt-5 text-sm md:text-[15px] text-ink/70 leading-[1.6] font-light"
                  style={{ fontFamily: geistFont }}
                >
                  {p.body}
                </p>
              </div>
            </article>
          ))}
        </div>
      </Section>

      {/* Universal footer */}
      <CaseStudyFooter
        label="What a Content Engine buys a brand"
        columns={[
          {
            eyebrow: "Consistency & Velocity",
            title: "One look, at the speed of a brief",
            subtitle: "The same world, ready for the next idea.",
            items: [
              "One look, held across hundreds of assets and every market",
              "New work in the same world on demand, no re-briefing from zero",
            ],
          },
          {
            eyebrow: "Ownership & Efficiency",
            title: "A system that keeps paying",
            subtitle: "The cost curve bends as volume goes up.",
            items: [
              "A system that keeps paying, campaign after campaign",
              "The cost curve bends down as volume goes up",
            ],
          },
        ]}
      />
    </div>
  );
};

export const CASE_STUDY_BODIES = {
  hybrid: HybridBody,
  ai: AiLedBody,
  engines: EnginesBody,
};

export default CASE_STUDY_BODIES;
